/*
 * ===============================================================================================================
 *                                Copyright 2020, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React from 'react';
import { Box, Grid, ListItemAvatar, ListItemText, ListItemSecondaryAction } from '@mui/material';
import clsx from 'clsx';
import useStyles from './styles';
import { LuiSemanticIndicator, PriorityType } from '@jda/lui-common-component-library-mui5';

export interface CustomListItemProps {
  /**
   * Optional: Custom className can be passed which will on the root node of listitem. This classname can be used as a reference of making any custom styling changes.
   */
  className?: string;
  /**
   * Optional: prop to enable wrapping of secondary content and show in 2 lines.
   */
  wrapText?: boolean;
  /**
   * Required: prop to enable/Show Priority bar if 'keyPriority' applies.
   */
  enablePriorityBar: boolean;
  /**
   * Required: prop to enable/Show Priority bar if 'keyPriority' applies.
   */
  priorityItemKey?: PriorityType;
  /**
   * Required: prop to enable/Show the listItem for specific view. E.g if the design has icon or avatar
   */
  showAvatarContent?: boolean;
  /**
   * Optional: func. prop to return content over the primary section.
   */
  ListOverlineContent?: React.ReactNode;
  /**
   * Optional: func. prop to return avatar content. If Avatar needs to be shown in the every listItem
   */
  ListAvatarContent?: React.ReactNode;
  /**
   * Optional: func. prop to return Primary content.
   */
  ListPrimaryContent?: React.ReactNode;
  /**
   * Optional: func. prop to return Secondary content below the primary content.
   */
  ListSecondaryContent?: React.ReactNode;
  /**
   * Optional: func. prop to return Secondary Action content on the right corner of list item.
   */
  ListSecondaryActionContent?: React.ReactNode;
}

export const CustomListItem: React.FC<CustomListItemProps> = ({
  enablePriorityBar,
  priorityItemKey,
  showAvatarContent,
  className,
  ListAvatarContent,
  ListPrimaryContent,
  ListSecondaryContent,
  wrapText,
  ListOverlineContent,
  ListSecondaryActionContent
}: any) => {
  const { classes } = useStyles();
  return (
    <Box
      className={clsx(classes.root, className)}
      display="flex"
      alignContent="flex-start"
      position="relative"
      component="div"
      minWidth={'100%'}
    >
      {Boolean(enablePriorityBar && priorityItemKey) && (
        <LuiSemanticIndicator state={priorityItemKey} thickness={2.5} height={'100%'} />
      )}
      {showAvatarContent && (
        <ListItemAvatar className={classes.avatarWrapper}>
          <Box className={classes.avatarDisplay}>{ListAvatarContent}</Box>
        </ListItemAvatar>
      )}
      <ListItemText
        disableTypography={true}
        secondary={ListSecondaryContent}
        inset={Boolean(enablePriorityBar) && !Boolean(showAvatarContent)}
        className={`${!wrapText ? classes.wrappingText : ''}`}
      >
        <React.Fragment>
          <Grid container direction="column">
            {ListOverlineContent && <Grid item>{ListOverlineContent}</Grid>}
            {ListPrimaryContent && <Grid item>{ListPrimaryContent}</Grid>}
          </Grid>
        </React.Fragment>
      </ListItemText>
      {ListSecondaryActionContent && (
        <ListItemSecondaryAction className={classes.secondaryAction}>
          {ListSecondaryActionContent}
        </ListItemSecondaryAction>
      )}
    </Box>
  );
};
CustomListItem.defaultProps = {
  enablePriorityBar: true,
  showAvatarContent: true
};

export default CustomListItem;
